import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import helpers from "helpers/index";
import queryClient, { IQueryParams } from 'queries';
import { TypedUser } from "interface/user.model";
import { TypedOrders } from "./orders.query";
import { TypedTrip_manifest_invoice } from "./trip_manifest_invoice.query";


/** 
 * Khi xuất hóa đơn xong là con mẹ nó phải truy quét lại
 * Nếu thấy status là *** thì phải quét lại mỗi 15 phút một lần ...
 * Phải có trường lưu trữ thông tin lại
 * Trường mặc định là NHAP
 */
export enum TThaiHDon {
    /** Tất cả (Bao gồm hóa đơn nháp) */
    TAT_CA_BAO_GOM_NHAP = -1,

    /** Tất cả (Không lấy hóa đơn nháp) */
    TAT_CA_KHONG_NHAP = 0,

    /** Nháp */
    NHAP = 1,

    /** Lỗi ký */
    LOI_KY = 2,

    /** Chưa gửi CQT */
    CHUA_GUI_CQT = 3,

    /** Đã cấp mã/tiếp nhận */
    DA_CAP_MA_TIEP_NHAN = 4,

    /** CQT chưa phản hồi */
    CQT_CHUA_PHAN_HOI = 5,

    /** Không đủ điều kiện cấp mã/tiếp nhận */
    KHONG_DU_DK_CAP_MA_TIEP_NHAN = 6,
}

/**
 * Status bằng chữ, cho dễ hiểu, ví dụ const text = TThaiHDonLabel[TThaiHDon.LOI_KY];
 */
export const TThaiHDonLabel: Record<TThaiHDon, string> = {
    [TThaiHDon.TAT_CA_BAO_GOM_NHAP]: "Mặc định 2",
    [TThaiHDon.TAT_CA_KHONG_NHAP]: "Mặc định",
    [TThaiHDon.NHAP]: "Nháp",
    [TThaiHDon.LOI_KY]: "Lỗi ký",
    [TThaiHDon.CHUA_GUI_CQT]: "Chưa gửi CQT",
    [TThaiHDon.DA_CAP_MA_TIEP_NHAN]: "Đã cấp mã/tiếp nhận",
    [TThaiHDon.CQT_CHUA_PHAN_HOI]: "CQT chưa phản hồi",
    [TThaiHDon.KHONG_DU_DK_CAP_MA_TIEP_NHAN]:
        "Không đủ đk cấp mã/tiếp nhận",
};

export interface TypedInvoices {
    invoice_id?: string;
    maTraCuu?: string;
    MTChieu?: string;
    maSoHDon?: string;
    khhDon?: string;
    urlDownloadPDF?: string; // 
    is_signed?: number; // 1 là đã ký, 0 là chưa ký
    LoaiHDon?: number; // 1: bản chính thức, 0: bản nháp
    TaxType?: string; // Loại thuế viết cho: COMPANY hoặc PERSONAL
    NMua_Ten?: string;
    NMua_HVTNMHang?: string;
    NMua_CCCDan?: string;
    NMua_MST?: string;
    NMua_DChi?: string;
    NMua_SDThoai?: string;
    NMua_DCTDTu?: string;
    shDon?: string;

    /** Cơ quan thuế sẽ bắn status về đấy ... */
    maTThaiHDon?: TThaiHDon;
    ghiChuCQT?: string; /** Ghi chú của cơ quan thuế ... */
    mccqt?: string; /** Mã tiếp nhận của cơ quan thuế, phải có, nếu không là chưa xuất thành công ... */


    DSHHDVu?: string; // danh sách dịch vụ
    TgThTien?: number; // tổng giá
    TgTThue?: number; // tổng thuế
    TgTTTBSo?: number; // tổng tiền
    // order_id?: string; // trường này nội bộ không dùng

    exportMode?: "each_order" | "contract",

    createdAt?: string;
    signedAt?: string;

    invoice_parent?: bigint;

    author_data?: TypedUser // ký bởi ai?
    signed_data?: TypedUser // ký bởi ai?

    invoice_status?: ENUM_INVOICE_STATUS; /** Status nội bộ */

    trip_manifest_invoice?: TypedTrip_manifest_invoice;

    order_to_invoice?: {
        order_id?: bigint,
        invoice_id?: bigint,
        invoices?: TypedInvoices,
        orders?: TypedOrders
    }[]

    invoice_parent_data?: {
        invoice_id?: string,
        shDon?: string,
        maSoHDon?: string,
    }


}


/** trường này để đánh dấu hóa đơn là mặc định khi xuất, hoặc đánh dấu hủy thay thế ...
 * Khi thay thế, trường invoice_parent sẽ được fill ID ...
 * */
export enum ENUM_INVOICE_STATUS {
    NORMAL = 0, // mặc định, hợp lệ
    CANCEL = 9, // Đã hủy 
}



type IQuery = TypedInvoices & IQueryParams;




/** truy vấn tới /general_analytics để lấy dữ liệu hiển thị trên tabs, interface: 
    not_signed: number,
    signed: number,
 */
export function useGetInvoices_analytics() {
    return useQuery({
        queryKey: ["invoices/fetch_entity_analytics"],
        queryFn: () => axios.get<{ not_signed: number, signed: number, signed_error: number, signed_success: number }>(`/invoices/general_analytics`).then(response => response.data),
        retry: 1,
    });
}

/*
* List of all invoice
*/
export function useGetInvoices(object: IQuery) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["invoices/fetch_entity_list", object],
        queryFn: () => axios.get<{
            sum: {
                count: number;
                TgThTien: number;
                TgTThue: number;
                TgTTTBSo: number;
            }
            , records: TypedInvoices[]
        }>(`/invoices/${EndURL}`).then(response => {
            let { data, headers } = response;
            return {
                body: data,
                totalItems: Number(headers['x-total-count'] || 0)
            }
        }),
    });
}

/** Lấy các invoice Lỗi ra */
export function useGetInvoicesError(object: IQuery) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["invoices/fetch_entity_list_error", object],
        queryFn: () => axios.get<{
            sum: {
                count: number;
                TgThTien: number;
                TgTThue: number;
                TgTTTBSo: number;
            }
            , records: TypedInvoices[]
        }>(`/invoices/export_error/${EndURL}`).then(response => {
            let { data, headers } = response;
            return {
                body: data,
                totalItems: Number(headers['x-total-count'] || 0)
            }
        }),
    });
}

/** lấy thông tin một invoice ra  */
export function useGetInvoice(invoice_id: bigint) {
    return useQuery({
        queryKey: ["invoices/fetch_entity", invoice_id],
        queryFn: () => axios.get<TypedInvoices>(`/invoices/${invoice_id}`).then(response => response.data),
        enabled: !!invoice_id
    })
}



export function useMutateUpdateInvoice() {
    return useMutation({
        mutationKey: ["invoices/update_entity"],
        mutationFn: ({ invoice_id, ...body }: TypedInvoices) => axios.patch<TypedInvoices>(`/invoices/${invoice_id}`, body).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_list'] });
        },
    });
}




export function useMutateDeleteInvoices() {
    return useMutation({
        mutationKey: ["invoices/trip_manifest_invoice/delete"],
        mutationFn: (invoice_id) => axios.delete<void>(`/invoices/${invoice_id}`).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_analytics'] });
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        },
    });
}


export function useMutateSignInvoices() {
    return useMutation({
        mutationKey: ["invoices/trip_manifest_invoice/sign"],
        mutationFn: (invoice_id) => axios.put<void>(`/invoices/sign/${invoice_id}`).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_analytics'] });
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
        },
    });
}

export function useMutatePreviewInvoices() {
    return useMutation({
        mutationKey: ["invoices/trip_manifest_invoice/preview"],
        mutationFn: (trip_manifest_id) => axios.get<any>(`/invoices/export/preview/${trip_manifest_id}`).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_list'] });

            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        },
    });
}

export function useMutateExportInvoices() {
    return useMutation({
        mutationKey: ["invoices/trip_manifest_invoice/export_invoice"],
        mutationFn: (trip_manifest_id) => axios.get<TypedInvoices>(`/invoices/export/certified/${trip_manifest_id}`).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_analytics'] });
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        },
    });
}

/** để check tình trạng hóa đơn do cơ quan thuế cấp, cần phải gọi qua endpoint này ... */
export function useMutateGetCurrentInvoiceStatus() {
    return useMutation({
        mutationKey: ["invoices/fetch_entity_status"],
        mutationFn: (invoice_id) => axios.get<TypedInvoices>(`/invoices/current_invoice_status/${invoice_id}`).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_analytics'] });
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_list'] });
        },
    });
}

/** để check tình trạng hóa đơn do cơ quan thuế cấp, cần phải gọi qua endpoint này ... */
export function useMutateReExportInvoices() {
    return useMutation({
        mutationKey: ["invoices/reexport_invoices"],
        mutationFn: (invoice_id) => axios.get<TypedInvoices>(`/invoices/reexport_invoices/${invoice_id}`).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_analytics'] });
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['invoices/fetch_entity_list_error'] });
        },
    });
}